import type { AnimatedIconComponent } from '../../components/icons';
import {
  BriefcaseBusinessIcon,
  CalendarDaysIcon,
  ChartSplineIcon,
  FileStackIcon,
  FileTextIcon,
  FolderOpenIcon,
  HandCoinsIcon,
  LayoutGridIcon,
  SparklesIcon,
  UserIcon,
  UsersIcon,
  VideoIcon,
} from '../../components/icons';

/** @deprecated Use NavEntry for sidebar config */
export type NavItem = {
  label: string;
  path: string;
  icon: AnimatedIconComponent;
  badge?: string;
};

export type NavChild = {
  label: string;
  path: string;
  icon: AnimatedIconComponent;
  badge?: string;
};

export type NavLinkEntry = {
  type: 'link';
  label: string;
  path: string;
  icon: AnimatedIconComponent;
  badge?: string;
};

export type NavGroupEntry = {
  type: 'group';
  label: string;
  icon: AnimatedIconComponent;
  items: NavChild[];
};

export type NavDividerEntry = {
  type: 'divider';
};

export type NavEntry = NavLinkEntry | NavGroupEntry | NavDividerEntry;

/** All possible service nav children keyed by feature ID */
const JOB_SEEKER_SERVICE_MAP: Record<string, NavChild> = {
  'resume-review': { label: 'Resume Review', path: '/jobseeker-dashboard/resume-review', icon: FileTextIcon },
  'culture-call': { label: 'Mock Interview', path: '/jobseeker-dashboard/mock-interview', icon: VideoIcon },
  'referral-session': { label: 'Referral Session', path: '/jobseeker-dashboard/referral-session', icon: UsersIcon },
};

const REFERRER_JOBSEEKER_SERVICE_MAP: Record<string, NavChild> = {
  'resume-review': {
    label: 'Resume Review',
    path: '/referral-dashboard/jobseeker-dashboard/resume-review',
    icon: FileTextIcon,
  },
  'culture-call': {
    label: 'Mock Interview',
    path: '/referral-dashboard/jobseeker-dashboard/mock-interview',
    icon: VideoIcon,
  },
  'referral-session': {
    label: 'Referral Session',
    path: '/referral-dashboard/jobseeker-dashboard/referral-session',
    icon: UsersIcon,
  },
};

/** Build job-seeker nav filtered to only enabled features */
export function buildJobSeekerNav(enabledFeatures: string[]): NavEntry[] {
  const services = Object.entries(JOB_SEEKER_SERVICE_MAP)
    .filter(([key]) => enabledFeatures.includes(key))
    .map(([, child]) => child);

  return [
    { type: 'link', label: 'Dashboard', path: '/jobseeker-dashboard', icon: LayoutGridIcon },
    { type: 'link', label: 'AI Grader', path: '/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
    { type: 'divider' },
    ...(services.length > 0
      ? [{ type: 'group' as const, label: 'Services', icon: FolderOpenIcon, items: services }]
      : []),
    { type: 'link', label: 'My Applications', path: '/jobseeker-dashboard/my-applications', icon: FileStackIcon },
    { type: 'link', label: 'Job Openings', path: '/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
    { type: 'link', label: 'My Sessions', path: '/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
    { type: 'link', label: 'Weekly Drops', path: '/jobseeker-dashboard/referral-drops', icon: HandCoinsIcon },
    { type: 'link', label: 'Invoices', path: '/jobseeker-dashboard/invoices', icon: FileTextIcon },
    { type: 'link', label: 'My Profile', path: '/jobseeker-dashboard/profile', icon: UserIcon },
  ];
}

/** Build employee-as-jobseeker nav filtered to only enabled features */
export function buildEmployeeAsJobseekerNav(enabledFeatures: string[]): NavEntry[] {
  const services = Object.entries(REFERRER_JOBSEEKER_SERVICE_MAP)
    .filter(([key]) => enabledFeatures.includes(key))
    .map(([, child]) => child);

  return [
    { type: 'link', label: 'Dashboard', path: '/referral-dashboard/jobseeker-dashboard', icon: LayoutGridIcon },
    { type: 'link', label: 'AI Grader', path: '/referral-dashboard/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
    { type: 'divider' },
    ...(services.length > 0
      ? [{ type: 'group' as const, label: 'Services', icon: FolderOpenIcon, items: services }]
      : []),
    { type: 'link', label: 'My Applications', path: '/referral-dashboard/jobseeker-dashboard/my-applications', icon: FileStackIcon },
    { type: 'link', label: 'Job Openings', path: '/referral-dashboard/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
    { type: 'link', label: 'My Sessions', path: '/referral-dashboard/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
    { type: 'link', label: 'Weekly Drops', path: '/referral-dashboard/jobseeker-dashboard/referral-drops', icon: HandCoinsIcon },
    { type: 'link', label: 'Invoices', path: '/referral-dashboard/jobseeker-dashboard/invoices', icon: FileTextIcon },
    { type: 'link', label: 'My Profile', path: '/referral-dashboard/jobseeker-dashboard/profile', icon: UserIcon },
  ];
}

/**
 * Career Trust candidate nav, gated by the server-side trust feature flags.
 * Returns an empty list when no trust surface is enabled, so the group only
 * appears once a flag is turned on.
 */
export function buildCandidateTrustNav(
  flags: Record<string, boolean>,
  base: '/jobseeker-dashboard' | '/referral-dashboard/jobseeker-dashboard'
): NavEntry[] {
  const items: NavChild[] = [];
  if (flags.career_proof_layer) {
    items.push({ label: 'Career Proof', path: `${base}/career-proof`, icon: FileStackIcon });
  }
  if (flags.referral_passport) {
    items.push({ label: 'Passports', path: `${base}/passports`, icon: FileTextIcon });
  }
  if (items.length === 0) return [];
  return [
    { type: 'divider' },
    { type: 'group', label: 'Career Trust', icon: FileStackIcon, items },
  ];
}

/** Career Trust professional nav (verification inbox), gated by the flag. */
export function buildProfessionalTrustNav(flags: Record<string, boolean>): NavEntry[] {
  if (!flags.human_verification) return [];
  return [{ type: 'link', label: 'Verification', path: '/referral-dashboard/verification-requests', icon: UsersIcon }];
}

/** Static fallbacks (all features enabled) for backwards compatibility */
export const JOB_SEEKER_NAV: NavEntry[] = buildJobSeekerNav(['resume-review', 'culture-call', 'referral-session']);

export const EMPLOYEE_AS_JOBSEEKER_NAV: NavEntry[] = buildEmployeeAsJobseekerNav([
  'resume-review',
  'culture-call',
  'referral-session',
]);

/** Flat list kept for legacy references */
export const JOB_SEEKER_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/jobseeker-dashboard', icon: LayoutGridIcon },
  { label: 'AI Grader', path: '/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
  { label: 'Resume Review', path: '/jobseeker-dashboard/resume-review', icon: FileTextIcon },
  { label: 'Mock Interview', path: '/jobseeker-dashboard/mock-interview', icon: VideoIcon },
  { label: 'Referral Session', path: '/jobseeker-dashboard/referral-session', icon: UsersIcon },
  { label: 'Job Openings', path: '/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
  { label: 'My Sessions', path: '/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
  { label: 'Invoices', path: '/jobseeker-dashboard/invoices', icon: FileTextIcon },
  { label: 'My Profile', path: '/jobseeker-dashboard/profile', icon: UserIcon },
];

export const EMPLOYEE_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/referral-dashboard', icon: LayoutGridIcon },
  { label: 'My Services', path: '/referral-dashboard/services', icon: BriefcaseBusinessIcon },
  { label: 'Post a Job', path: '/referral-dashboard/post-job', icon: BriefcaseBusinessIcon },
  { label: 'Payouts', path: '/referral-dashboard/payouts', icon: ChartSplineIcon },
  { label: 'Referral Drops', path: '/referral-dashboard/referral-drops', icon: HandCoinsIcon },
  { label: 'Invoices', path: '/referral-dashboard/invoices', icon: FileTextIcon },
  { label: 'My Sessions', path: '/referral-dashboard/my-sessions', icon: CalendarDaysIcon },
  { label: 'My Profile', path: '/referral-dashboard/profile', icon: UserIcon },
];

export const EMPLOYEE_AS_JOBSEEKER_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/referral-dashboard/jobseeker-dashboard', icon: LayoutGridIcon },
  { label: 'AI Grader', path: '/referral-dashboard/jobseeker-dashboard/resume-grader', icon: SparklesIcon },
  { label: 'Resume Review', path: '/referral-dashboard/jobseeker-dashboard/resume-review', icon: FileTextIcon },
  { label: 'Mock Interview', path: '/referral-dashboard/jobseeker-dashboard/mock-interview', icon: VideoIcon },
  { label: 'Referral Session', path: '/referral-dashboard/jobseeker-dashboard/referral-session', icon: UsersIcon },
  { label: 'Job Openings', path: '/referral-dashboard/jobseeker-dashboard/jobopenings', icon: BriefcaseBusinessIcon },
  { label: 'My Sessions', path: '/referral-dashboard/jobseeker-dashboard/my-sessions', icon: CalendarDaysIcon },
  { label: 'Invoices', path: '/referral-dashboard/jobseeker-dashboard/invoices', icon: FileTextIcon },
  { label: 'My Profile', path: '/referral-dashboard/jobseeker-dashboard/profile', icon: UserIcon },
];

export function flatNavToEntries(items: NavItem[]): NavEntry[] {
  return items.map((item) => ({ type: 'link', ...item }));
}

export const EMPLOYEE_NAV: NavEntry[] = flatNavToEntries(EMPLOYEE_ITEMS);

export function findNavLabel(entries: NavEntry[], _pathname: string, isActive: (path: string) => boolean): string {
  for (const entry of entries) {
    if (entry.type === 'link' && isActive(entry.path)) {
      return entry.label;
    }
    if (entry.type === 'group') {
      const child = entry.items.find((item) => isActive(item.path));
      if (child) return child.label;
    }
  }
  return 'Dashboard';
}
